import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import {
  Button,
  Select,
  TextField,
  Typography,
} from '../../../components/atoms';
import { SelectOption } from '../../../components/atoms/Select';
import { Dialog } from '../../../components/layouts';
import {
  LocationArea,
  PickupLocationOption,
  PreOrderTime,
  TableServiceCategory,
} from '../../../weborder/Api';
import { BLANK } from '../../../weborder/constants/Constants';
import { basketSelectors } from '../../../weborder/redux';
import { checkoutUtils } from '../utils';
import ButtonPayStripe from './ButtonPayStripe';

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .fd-text__h1': {
      marginBottom: theme.spacing(1.75),

      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(2.5),
      },
    },
    '& .fd-text__caption': {
      marginBottom: theme.spacing(0.5),
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',

    '& option': {
      cursor: 'pointer',
      padding: theme.spacing(1.5, 2),

      '&:hover': {
        backgroundColor: theme.palette.grey[300],
      },
    },
  },
  paper: {
    marginTop: '1px',
  },
  selector: {
    marginBottom: theme.spacing(2),

    '& .MuiSelect-selectMenu, .MuiSelect-select': {
      padding: theme.spacing(1.625),
    },
    '&.MuiInputBase-root': {
      border: `1px solid ${theme.palette.grey[400]}`,
      position: 'relative',

      '&.Mui-focused': {
        border: `1px solid ${theme.palette.common.black}`,
      },
      '& .MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd': {
        position: 'absolute',
      },
      '& .MuiInputAdornment-positionStart': {
        left: theme.spacing(1.5),
      },
      '& .MuiSvgIcon-root': {
        fill: theme.palette.common.black,
        right: theme.spacing(1),
      },
    },
  },
}));

type PreOrderTimeProps = {
  isDelivery: boolean;
  isTableService: boolean;
  onChange(change: string): void;
  options: PreOrderTime[];
  value: string;
};

type NameInputProps = {
  hasRequestFullNameFeatureFlag: boolean;
  isDelivery: boolean;
  isTableService: boolean;
  onChange(change: string): void;
  value: string;
};

type TableNumberInputProps = {
  category: TableServiceCategory;
  locationAreaOptions?: LocationArea[];
  onChange(change?: number): void;
  options?: PickupLocationOption[];
  value?: number;
};

type SelectTimeDialogProps = {
  checkoutValidationErrors: WebOrder.State.BasketValidationError[];
  disabled?: boolean;
  nameInputProps?: NameInputProps;
  onCancel: () => void;
  onOk: () => void;
  preOrderTimeProps?: PreOrderTimeProps;
  selectedPayment: ReturnType<
    typeof basketSelectors.getSelectedPaymentOrDefault
  >;
  show: boolean;
  tableNumberInputProps?: TableNumberInputProps;
  translate: any;
};

export function SelectTimeDialog(props: SelectTimeDialogProps): JSX.Element {
  const {
    checkoutValidationErrors,
    disabled,
    nameInputProps,
    onCancel,
    onOk,
    preOrderTimeProps,
    selectedPayment,
    tableNumberInputProps,
    translate,
    show,
  } = props;

  const classes = useStyles();

  const handleCancel = (): void => onCancel();
  const handleConfirm = (): void => onOk();

  const hasManyRequiredFields = checkoutValidationErrors.length > 1;

  const renderPreOrderTimeSelect = (
    timeProps: PreOrderTimeProps,
  ): JSX.Element => {
    const { isDelivery, isTableService, onChange, options, value } = timeProps;

    const getPreOrderTimeTitle = (): string => {
      if (isDelivery) {
        return translate('When_do_you_want_your_order_delivered');
      }
      if (isTableService) {
        return translate('When_do_you_want_to_receive_your_order');
      }

      return translate('When_do_you_want_to_collect');
    };

    const handleChangePreorder = (event): void => {
      onChange(event.target.value);
    };

    const preorderOptions = options.map(
      (option): SelectOption => ({
        disabled: option.IsFull,
        label: option.IsFull
          ? `${option.Value} (${translate('Full')})`
          : option.Value,
        key: option.Key,
      }),
    );
    preorderOptions.unshift({
      disabled: true,
      label: translate('Select_a_time'),
      key: BLANK,
    });

    return (
      <>
        <Typography variant={hasManyRequiredFields ? 'caption' : 'h1'}>
          {getPreOrderTimeTitle()}
        </Typography>

        <Select
          aria-label={translate('When_do_you_want_to_collect')}
          className={classes.selector}
          fullWidth
          id="selectTime"
          menuItems={preorderOptions}
          onChange={handleChangePreorder}
          value={value.toString()}
        />
      </>
    );
  };

  const renderNameInput = (nameProps: NameInputProps): JSX.Element => {
    const {
      hasRequestFullNameFeatureFlag,
      isDelivery,
      isTableService,
      onChange,
      value,
    } = nameProps;

    const orderType = isDelivery ? 'delivery' : 'collection';
    const nameFieldTitle = checkoutUtils.getNameTitle(
      hasRequestFullNameFeatureFlag,
      orderType,
      isTableService,
      translate,
    );

    return (
      <>
        <Typography
          data-fd="dialog-select-time-title"
          variant={hasManyRequiredFields ? 'caption' : 'h1'}
        >
          {nameFieldTitle}
        </Typography>
        <TextField
          fullWidth
          inputProps={{
            name: 'name_input',
            id: 'For_when_you_collect',
          }}
          onChange={(e): void => {
            onChange(e.target.value);
          }}
          placeholder={translate('Checkout_SelectTimeDialog_EnterYourName')}
          value={value}
        />
      </>
    );
  };

  const renderTableNumberInput = (
    locationProps: TableNumberInputProps,
  ): JSX.Element => {
    const {
      category,
      locationAreaOptions = [],
      onChange,
      options = [],
      value = '',
    } = locationProps;

    const tableListOptions = checkoutUtils.getTableListOptions(
      locationAreaOptions,
      options,
    );

    tableListOptions.unshift({
      disabled: true,
      label: translate('Select_an_option'),
      key: 'default',
    });

    const tableNumberOrDefault = value || 'default';
    const selectedOption = tableListOptions.find(
      (option) => option.key === tableNumberOrDefault.toString(),
    );

    const handleLocationChange = (event): void => {
      onChange(parseInt(event.target.value) || undefined);
    };

    const hasTableListOptions = tableListOptions.length > 1;

    return (
      <>
        <Typography variant={hasManyRequiredFields ? 'caption' : 'h1'}>
          {`${translate(`Enter_table_number_${category}`)}`}
        </Typography>

        {hasTableListOptions ? (
          <Select
            aria-label={translate(`Table_number_${category}`) as string}
            className={classes.selector}
            fullWidth
            id="tableNumber"
            menuItems={tableListOptions}
            name="tableNumber"
            onChange={handleLocationChange}
            selected={selectedOption}
          />
        ) : (
          <TextField
            inputProps={{
              id: 'tableNumber',
              inputMode: 'numeric',
              name: 'tableNumber',
              pattern: '[0-9]*',
              'data-fd': 'tableNumber_input',
            }}
            onChange={(e): void => {
              onChange(
                parseInt(e.target.value.replace(/\D/g, '')) || undefined,
              );
            }}
            placeholder={translate(`Table_number_${category}`) as string}
            value={typeof value === 'number' ? value : ''}
          />
        )}
      </>
    );
  };

  const dialogActions =
    selectedPayment && selectedPayment.paymentId === 'stripe' ? (
      <ButtonPayStripe
        data-fd="stripe_pay_confirm_button"
        disabled={disabled}
        fullWidth
        onClick={handleConfirm}
      >
        {translate('Confirm')}
      </ButtonPayStripe>
    ) : (
      <Button
        data-fd="Confirm"
        disabled={disabled}
        fullWidth
        onClick={handleConfirm}
      >
        {translate('Confirm')}
      </Button>
    );

  return (
    <Dialog
      actions={dialogActions}
      className={classes.dialog}
      dialogName="select-time"
      onTitleClick={handleCancel}
      open={show}
    >
      {hasManyRequiredFields && (
        <Typography variant="h1">
          {translate('Checkout_SelectTimeDialog_PleaseFillInMissingFields')}
        </Typography>
      )}

      {preOrderTimeProps && renderPreOrderTimeSelect({ ...preOrderTimeProps })}

      {nameInputProps && renderNameInput({ ...nameInputProps })}

      {tableNumberInputProps &&
        renderTableNumberInput({ ...tableNumberInputProps })}
    </Dialog>
  );
}
